/* eslint-disable max-lines, indent, complexity, max-statements */
import React, { useCallback, useEffect, useState } from 'react'
import { arrayOf, func, number, object } from 'prop-types'
import styled from 'styled-components'
import Modal from '../Modal/Modal'
import IcoPin from '../icons/IcoPin'
import IcoGrip from '../icons/IcoGrip'
import IcoUp from '../icons/IcoUp'
import IcoEdit from '../icons/IcoEdit'
import IcoTrash from '../icons/IcoTrash'
import { BtnDanger, BtnGhost, BtnPrimary } from '../ui/buttons'
import { Field, FieldError, Input, Label, Row2 } from '../ui/fields'
import {
  cadastraEndereco,
  cadastraEnderecoVigente,
  deletaEndereco,
  editaEndereco,
  getEnderecoPorCEP,
  listaEnderecos
} from '../../../../api/endereco.servico'
import { theme } from '../../styles'
import {
  Callout,
  SuccessBox,
  SuccessSeal
} from './cancel-flow-styles'
import {
  ItemList,
  Itemrow,
  ItemrowGrip,
  ItemrowBody,
  ItemrowTags,
  ItemrowActs,
  IconBtn,
  Tag,
  EmptyList,
  Listnote,
  OnlyDesktop,
  OnlyMobile
} from './payment-flow-styles'

const RowCep = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 12px;

  ${theme.mobile} {
    grid-template-columns: 1fr;
  }
`

const RowCidade = styled.div`
  display: grid;
  grid-template-columns: 1fr 90px;
  gap: 12px;
`

const LoadingBox = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: ${theme.cores.inkSoft};
  font-size: 14px;
`

const BLANK = {
  'cep': '',
  'rua': '',
  'numero': '',
  'complemento': '',
  'bairro': '',
  'cidade': '',
  'uf': ''
}

const MSG_ERRO_GENERICO = 'Não foi possível concluir a operação. Tente novamente.'

function formatCep( value ) {
  const digits = value.replace( /\D/gu, '' ).slice( 0, 8 )
  return digits.length > 5 ? `${digits.slice( 0, 5 )}-${digits.slice( 5 )}` : digits
}

function extractMensagemErro( erro ) {
  const data = erro?.response?.data
  if ( Array.isArray( data?.errors ) && data.errors[ 0 ]?.message ) {
    return data.errors[ 0 ].message
  }
  if ( typeof data === 'string' && data.length > 0 && data.length < 200 ) {
    return data
  }
  return MSG_ERRO_GENERICO
}

function isVigenteDaAssinatura( addr, assinaturaId ) {
  if ( !addr?.vigencia?.assinaturas || !assinaturaId ) {
    return false
  }
  return addr.vigencia.assinaturas.includes( assinaturaId )
}

function ordenarEnderecos( lista, assinaturaId ) {
  if ( !Array.isArray( lista ) ) {
    return []
  }
  const vigentes = lista.filter( ( item ) => isVigenteDaAssinatura( item, assinaturaId ) )
  const outros = lista.filter( ( item ) => !isVigenteDaAssinatura( item, assinaturaId ) )
  return [ ...vigentes, ...outros ]
}

function toApiPayload( form ) {
  const payload = {
    'cep': form.cep.replace( /\D/gu, '' ),
    'uf': form.uf,
    'cidade': form.cidade,
    'bairro': form.bairro,
    'rua': form.rua,
    'numero': form.numero,
    'complemento': form.complemento
  }
  if ( form.ibge ) {
    payload.ibge = form.ibge
  }
  return payload
}

export default function AddressManager( {
  addrsInitial,
  assinaturaId,
  onAddrs,
  onEnderecoVigenteChange,
  onClose,
  toast
} ) {
  const [ view, setView ] = useState( 'list' )
  const [ editing, setEditing ] = useState( null )
  const [ target, setTarget ] = useState( null )
  const [ form, setForm ] = useState( BLANK )
  const [ err, setErr ] = useState( {} )
  const [ addrs, setAddrs ] = useState( addrsInitial || [] )
  const [ loading, setLoading ] = useState( true )
  const [ submitting, setSubmitting ] = useState( false )
  const [ settingPrimaryId, setSettingPrimaryId ] = useState( null )
  const [ deleting, setDeleting ] = useState( false )
  const [ ultimoSalvo, setUltimoSalvo ] = useState( null )
  const [ cepLoading, setCepLoading ] = useState( false )
  const [ dragId, setDragId ] = useState( null )
  const [ overId, setOverId ] = useState( null )

  const carregarLista = useCallback( async() => {
    try {
      const resp = await listaEnderecos()
      const lista = Array.isArray( resp?.data ) ? resp.data : []
      const ordenada = ordenarEnderecos( lista, assinaturaId )
      setAddrs( ordenada )
      onAddrs( ordenada )
      return ordenada
    } catch ( erro ) {
      toast( extractMensagemErro( erro ) )
      return null
    }
  }, [ assinaturaId, onAddrs, toast ] )

  useEffect( () => {
    let cancelado = false
    setLoading( true )
    carregarLista().finally( () => {
      if ( !cancelado ) {
        setLoading( false )
      }
    } )
    return () => {
      cancelado = true
    }
  }, [ carregarLista ] )

  function set( key ) {
    return ( event ) => setForm( ( prev ) => ( { ...prev, [ key ]: event.target.value } ) )
  }

  async function handleCepBlur() {
    const digits = form.cep.replace( /\D/gu, '' )
    if ( digits.length !== 8 ) {
      return
    }
    setCepLoading( true )
    try {
      const enderecoGerado = await getEnderecoPorCEP( digits )
      setForm( ( prev ) => ( { ...prev, ...enderecoGerado, 'cep': formatCep( enderecoGerado.cep || digits ) } ) )
      setErr( ( prev ) => ( { ...prev, 'cep': null } ) )
    } catch ( _erro ) {
      setErr( ( prev ) => ( { ...prev, 'cep': 'CEP não encontrado' } ) )
    } finally {
      setCepLoading( false )
    }
  }

  function openNew() {
    setEditing( null )
    setForm( BLANK )
    setErr( {} )
    setView( 'form' )
  }

  function openEdit( addr ) {
    setEditing( addr )
    setForm( { ...BLANK, ...addr } )
    setErr( {} )
    setView( 'form' )
  }

  function validate() {
    const next = {}
    if ( !( /^\d{5}-?\d{3}$/u ).test( form.cep ) ) {
      next.cep = 'CEP inválido'
    }
    if ( form.rua.trim().length < 3 ) {
      next.rua = 'Informe a rua'
    }
    if ( !form.numero.trim() ) {
      next.numero = 'Nº obrigatório'
    }
    if ( form.cidade.trim().length < 2 ) {
      next.cidade = 'Cidade'
    }
    if ( form.uf.trim().length !== 2 ) {
      next.uf = 'UF'
    }
    setErr( next )
    return Object.keys( next ).length === 0
  }

  async function applySave() {
    if ( submitting ) {
 return
}
    setSubmitting( true )
    try {
      const body = {
        'endereco': editing
          ? { ...toApiPayload( form ), 'id': editing.id }
          : toApiPayload( form ),
        'assinatura': assinaturaId
      }
      if ( editing ) {
        await editaEndereco( body )
      } else {
        await cadastraEndereco( body )
      }
      setUltimoSalvo( form )
      const lista = await carregarLista()
      if ( lista ) {
        const vigenteAtual = lista.find( ( item ) => isVigenteDaAssinatura( item, assinaturaId ) )
        if ( vigenteAtual ) {
          onEnderecoVigenteChange( vigenteAtual )
        }
      }
      setView( 'saved' )
    } catch ( erro ) {
      toast( extractMensagemErro( erro ) )
    } finally {
      setSubmitting( false )
    }
  }

  async function makePrimary( addr ) {
    if ( settingPrimaryId ) {
 return
}
    setSettingPrimaryId( addr.id )
    try {
      await cadastraEnderecoVigente( {
        'assinatura_id': assinaturaId,
        'endereco': { 'id': addr.id }
      } )
      await carregarLista()
      onEnderecoVigenteChange( addr )
      toast( 'Endereço principal alterado.' )
    } catch ( erro ) {
      toast( extractMensagemErro( erro ) )
    } finally {
      setSettingPrimaryId( null )
    }
  }

  function handleDrop( destino ) {
    const origemId = dragId
    setDragId( null )
    setOverId( null )
    if ( !origemId || origemId === destino.id || settingPrimaryId ) {
      return
    }
    const origemIndex = addrs.findIndex( ( item ) => item.id === origemId )
    const destinoIndex = addrs.findIndex( ( item ) => item.id === destino.id )
    if ( origemIndex < 0 || destinoIndex < 0 ) {
      return
    }
    const reordenada = [ ...addrs ]
    const [ movido ] = reordenada.splice( origemIndex, 1 )
    reordenada.splice( destinoIndex, 0, movido )
    setAddrs( reordenada )
    if ( reordenada[ 0 ].id !== addrs[ 0 ].id ) {
      makePrimary( reordenada[ 0 ] )
    }
  }

  async function handleDelete() {
    if ( deleting || !target ) {
 return
}
    setDeleting( true )
    try {
      await deletaEndereco( target.id, assinaturaId )
      await carregarLista()
      setView( 'deleted' )
    } catch ( erro ) {
      toast( extractMensagemErro( erro ) )
      setView( 'list' )
    } finally {
      setDeleting( false )
    }
  }

  if ( view === 'saved' ) {
    const salvo = ultimoSalvo || form
    return (
      <Modal
        footer={ (
          <BtnPrimary
            onClick={ () => setView( 'list' ) }
            type="button"
          >
            Voltar aos endereços
          </BtnPrimary>
        ) }
        onClose={ onClose }
        title={ editing ? 'Endereço atualizado' : 'Endereço cadastrado' }
      >
        <SuccessBox>
          <SuccessSeal $orange>
            <IcoPin
              height="22"
              style={{ 'color': '#fff' }}
              width="22"
            />
          </SuccessSeal>
          <h2 className="serif">Pronto</h2>
          <p>
            Enviaremos as próximas edições para
            <b> { salvo.rua }, { salvo.numero } — { salvo.cidade }/{ salvo.uf }</b>.
          </p>
        </SuccessBox>
      </Modal>
    )
  }

  if ( view === 'deleted' ) {
    return (
      <Modal
        footer={ (
          <BtnPrimary
            onClick={ () => setView( 'list' ) }
            type="button"
          >
            Voltar aos endereços
          </BtnPrimary>
        ) }
        onClose={ onClose }
        title="Endereço excluído"
      >
        <SuccessBox>
          <SuccessSeal $stop>
            <IcoTrash
              height="22"
              style={{ 'color': '#fff' }}
              width="22"
            />
          </SuccessSeal>
          <h2 className="serif">Endereço removido</h2>
          <p>
            Removemos o endereço da sua lista. Mantenha ao menos um endereço para continuar recebendo as edições.
          </p>
        </SuccessBox>
      </Modal>
    )
  }

  if ( view === 'save' ) {
    return (
      <Modal
        canClose={ !submitting }
        eyebrow="Entrega"
        footer={ (
          <>
            <BtnGhost
              disabled={ submitting }
              onClick={ () => setView( 'form' ) }
              type="button"
            >Voltar
            </BtnGhost>
            <BtnPrimary
              disabled={ submitting }
              onClick={ applySave }
              type="button"
            >{ submitting ? 'Salvando…' : 'Confirmar' }
            </BtnPrimary>
          </>
        ) }
        onClose={ onClose }
        title="Confirmar endereço"
      >
        <Callout>
          <IcoPin
            height="20"
            style={{ 'color': theme.cores.accent, 'flex': 'none' }}
            width="20"
          />
          <span>
            { editing ? 'Salvar as alterações do endereço' : 'Cadastrar o endereço' }
            <b> { form.rua }, { form.numero }{ form.complemento ? ` · ${form.complemento}` : '' } — { form.cidade }/{ form.uf }, { form.cep }</b>?
          </span>
        </Callout>
      </Modal>
    )
  }

  if ( view === 'del' && target ) {
    return (
      <Modal
        canClose={ !deleting }
        eyebrow="Entrega"
        footer={ (
          <>
            <BtnGhost
              disabled={ deleting }
              onClick={ () => setView( 'list' ) }
              type="button"
            >Voltar
            </BtnGhost>
            <BtnDanger
              disabled={ deleting }
              onClick={ handleDelete }
              type="button"
            >{ deleting ? 'Excluindo…' : 'Excluir endereço' }
            </BtnDanger>
          </>
        ) }
        onClose={ onClose }
        title="Excluir endereço?"
      >
        <Callout>
          <IcoTrash
            height="20"
            style={{ 'color': theme.cores.statusStop, 'flex': 'none' }}
            width="20"
          />
          <span>
            Vamos remover o endereço <b>{ target.rua }, { target.numero } — { target.cidade }/{ target.uf }</b>.
            Você pode cadastrar outro quando quiser.
          </span>
        </Callout>
      </Modal>
    )
  }

  if ( view === 'form' ) {
    return (
      <Modal
        canClose={ !cepLoading }
        eyebrow="Entrega"
        footer={ (
          <>
            <BtnGhost
              disabled={ cepLoading }
              onClick={ () => setView( 'list' ) }
              type="button"
            >Voltar
            </BtnGhost>
            <BtnPrimary
              disabled={ cepLoading }
              onClick={ () => {
                if ( validate() ) {
                  setView( 'save' )
                }
              } }
              type="button"
            >
              { editing ? 'Salvar endereço' : 'Cadastrar endereço' }
            </BtnPrimary>
          </>
        ) }
        onClose={ onClose }
        sub="É para onde enviamos cada nova edição. Alterações valem a partir do próximo envio."
        title={ editing ? 'Editar endereço' : 'Cadastrar novo endereço' }
      >
        <RowCep>
          <Field>
            <Label>CEP</Label>
            <Input
              $err={ Boolean( err.cep ) }
              disabled={ cepLoading }
              inputMode="numeric"
              onBlur={ handleCepBlur }
              onChange={ ( event ) => setForm( ( prev ) => ( { ...prev, 'cep': formatCep( event.target.value ) } ) ) }
              placeholder="00000-000"
              value={ form.cep }
            />
            { err.cep ? <FieldError>{ err.cep }</FieldError> : null }
            { cepLoading ? <span style={{ 'color': theme.cores.inkSoft, 'fontSize': '12px' }}>Buscando CEP…</span> : null }
          </Field>
          <Field>
            <Label>Rua / Logradouro</Label>
            <Input
              $err={ Boolean( err.rua ) }
              disabled={ cepLoading }
              onChange={ set( 'rua' ) }
              placeholder="Rua, avenida…"
              value={ form.rua }
            />
            { err.rua ? <FieldError>{ err.rua }</FieldError> : null }
          </Field>
        </RowCep>
        <Row2>
          <Field>
            <Label>Número</Label>
            <Input
              $err={ Boolean( err.numero ) }
              disabled={ cepLoading }
              onChange={ set( 'numero' ) }
              placeholder="123"
              value={ form.numero }
            />
            { err.numero ? <FieldError>{ err.numero }</FieldError> : null }
          </Field>
          <Field>
            <Label>Complemento</Label>
            <Input
              disabled={ cepLoading }
              onChange={ set( 'complemento' ) }
              placeholder="Apto, bloco… (opcional)"
              value={ form.complemento }
            />
          </Field>
        </Row2>
        <Field>
          <Label>Bairro</Label>
          <Input
            disabled={ cepLoading }
            onChange={ set( 'bairro' ) }
            placeholder="Bairro"
            value={ form.bairro }
          />
        </Field>
        <RowCidade>
          <Field>
            <Label>Cidade</Label>
            <Input
              $err={ Boolean( err.cidade ) }
              disabled={ cepLoading }
              onChange={ set( 'cidade' ) }
              placeholder="Cidade"
              value={ form.cidade }
            />
            { err.cidade ? <FieldError>{ err.cidade }</FieldError> : null }
          </Field>
          <Field>
            <Label>UF</Label>
            <Input
              $err={ Boolean( err.uf ) }
              disabled={ cepLoading }
              maxLength={ 2 }
              onChange={ ( event ) => setForm( ( prev ) => ( { ...prev, 'uf': event.target.value.toUpperCase().slice( 0, 2 ) } ) ) }
              placeholder="RJ"
              value={ form.uf }
            />
            { err.uf ? <FieldError>{ err.uf }</FieldError> : null }
          </Field>
        </RowCidade>
      </Modal>
    )
  }

  // view === 'list'
  const alterandoPrincipal = Boolean( settingPrimaryId )
  const tituloAguardePrincipal = 'Aguarde a alteração do endereço principal'
  return (
    <Modal
      canClose={ !alterandoPrincipal }
      eyebrow="Entrega"
      footer={ (
        <>
          <BtnGhost
            disabled={ alterandoPrincipal }
            onClick={ onClose }
            type="button"
          >{ alterandoPrincipal ? 'Aguarde…' : 'Fechar' }
          </BtnGhost>
          <span style={{ 'flex': 1 }} />
          <BtnPrimary
            disabled={ alterandoPrincipal }
            onClick={ openNew }
            title={ alterandoPrincipal ? tituloAguardePrincipal : null }
            type="button"
          >Cadastrar novo endereço
          </BtnPrimary>
        </>
      ) }
      onClose={ onClose }
      sub="Gerencie para onde vamos enviar a sua próxima coleção."
      title="Endereços de entrega"
    >
      { loading ? <LoadingBox>Carregando seus endereços…</LoadingBox> : null }
      { !loading && addrs.length === 0 ? (
        <EmptyList>Nenhum endereço cadastrado. Adicione um para receber as edições.</EmptyList>
      ) : null }
      { !loading && addrs.length > 0 ? (
        <>
          <Listnote>
            <IcoPin
              height="15"
              width="15"
            />
            <span>
              O endereço <b>principal</b> é o endereço de entrega da sua coleção.
              { ' ' }
              <OnlyDesktop><b>Arraste um endereço para o topo</b> para torná-lo principal.</OnlyDesktop>
              <OnlyMobile>Toque na <b>seta ↑</b> de um endereço para torná-lo principal.</OnlyMobile>
            </span>
          </Listnote>
          <ItemList>
            { addrs.map( ( addr, indice ) => {
              const isPrincipal = indice === 0
              const isSettingPrimary = settingPrimaryId === addr.id
              return (
                <Itemrow
                  $drag={ !alterandoPrincipal }
                  $dragging={ dragId === addr.id }
                  $over={ overId === addr.id && dragId !== addr.id }
                  $primary={ isPrincipal }
                  $validating={ alterandoPrincipal }
                  draggable={ !alterandoPrincipal }
                  key={ addr.id }
                  onDragEnd={ () => {
                    setDragId( null )
                    setOverId( null )
                  } }
                  onDragLeave={ () => {
                    if ( overId === addr.id ) {
                      setOverId( null )
                    }
                  } }
                  onDragOver={ ( event ) => {
                    event.preventDefault()
                    if ( overId !== addr.id ) {
                      setOverId( addr.id )
                    }
                  } }
                  onDragStart={ ( event ) => {
                    setDragId( addr.id )
                    event.dataTransfer.effectAllowed = 'move'
                    event.dataTransfer.setData( 'text/plain', addr.id )
                  } }
                  onDrop={ ( event ) => {
                    event.preventDefault()
                    handleDrop( addr )
                  } }
                >
                  <ItemrowGrip
                    aria-hidden="true"
                    title="Arraste para reordenar"
                  >
                    <IcoGrip
                      height="16"
                      width="16"
                    />
                  </ItemrowGrip>
                  <ItemrowBody>
                    <ItemrowTags>
                      { isPrincipal
                        ? <Tag>principal</Tag>
                        : <Tag $off>inativo</Tag> }
                    </ItemrowTags>
                    <b>{ addr.rua }, { addr.numero }</b>
                    <span>
                      { addr.complemento ? `${addr.complemento} · ` : '' }
                      { addr.bairro ? `${addr.bairro} · ` : '' }
                      { addr.cidade }/{ addr.uf } · { addr.cep }
                    </span>
                  </ItemrowBody>
                  <ItemrowActs>
                    { !isPrincipal ? (
                      <IconBtn
                        aria-label="Tornar principal"
                        disabled={ isSettingPrimary || Boolean( settingPrimaryId ) }
                        onClick={ () => makePrimary( addr ) }
                        title={ isSettingPrimary ? 'Alterando…' : 'Tornar principal' }
                        type="button"
                      >
                        <IcoUp
                          height="16"
                          width="16"
                        />
                      </IconBtn>
                    ) : null }
                    <IconBtn
                      aria-label="Editar endereço"
                      disabled={ alterandoPrincipal }
                      onClick={ () => openEdit( addr ) }
                      title={ alterandoPrincipal ? tituloAguardePrincipal : 'Editar' }
                      type="button"
                    >
                      <IcoEdit
                        height="16"
                        width="16"
                      />
                    </IconBtn>
                    <IconBtn
                      $danger
                      aria-label="Excluir endereço"
                      disabled={ alterandoPrincipal }
                      onClick={ () => {
                        setTarget( addr )
                        setView( 'del' )
                      } }
                      title={ alterandoPrincipal ? tituloAguardePrincipal : 'Excluir' }
                      type="button"
                    >
                      <IcoTrash
                        height="16"
                        width="16"
                      />
                    </IconBtn>
                  </ItemrowActs>
                </Itemrow>
              )
            } ) }
          </ItemList>
        </>
      ) : null }
    </Modal>
  )
}

AddressManager.propTypes = {
  'addrsInitial': arrayOf( object ),
  'assinaturaId': number,
  'onAddrs': func.isRequired,
  'onEnderecoVigenteChange': func,
  'onClose': func.isRequired,
  'toast': func.isRequired
}

AddressManager.defaultProps = {
  'addrsInitial': [],
  'assinaturaId': null,
  'onEnderecoVigenteChange': () => {}
}
